/**
 * knowledgeBase.ts — read helpers for Support > Knowledge Base articles.
 *
 * Extracts the kb_articles fetch that previously lived inline in
 * KnowledgeBasePage so it can be reused by the ticket composer's "Insert KB
 * link" picker. Visibility is enforced server-side by RLS (employees see
 * public/employees, agents see + agents, admins see all) — these helpers add no
 * client-side visibility filter; a row the caller can't see simply isn't
 * returned (getArticle → null).
 *
 * Mirrors the other libs' conventions: a TABLE const + the fail() helper. Read
 * only — no mutations here, so no writeAudit.
 *
 * Public API:
 *   - listArticles()   live, published articles (newest first) — for the picker
 *   - getArticle(id)   one live article by id (author joined), or null
 */

import { supabase } from './supabase';
import type { KBArticle } from '../types';

const TABLE = 'kb_articles';

/**
 * Throw a tagged Error. Pass a PostgREST error as `from` to carry its diagnostic
 * fields through to the page — `details`/`hint` are usually the ones that name the
 * actual fix (e.g. hint: "Perhaps you meant 'campaign_id'" on a PGRST200). Plain
 * validation failures pass no `from` (or a bare code string) and are unaffected.
 */
function fail(
  message: string,
  from?: string | { code?: string; details?: string; hint?: string },
): never {
  const err = new Error(message) as Error & { code?: string; details?: string; hint?: string };
  if (typeof from === 'string') {
    err.code = from;
  } else if (from) {
    if (from.code) err.code = from.code;
    if (from.details) err.details = from.details;
    if (from.hint) err.hint = from.hint;
  }
  throw err;
}

/**
 * List live, PUBLISHED articles the caller can see (RLS-filtered), newest first.
 * Used by the "Insert KB link" picker — drafts are intentionally excluded so an
 * agent can't link a customer to an unpublished article.
 */
export async function listArticles(): Promise<KBArticle[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, author:profiles(id,full_name)')
    .eq('is_deleted', false)
    .eq('is_published', true)
    .order('created_at', { ascending: false });

  if (error) fail(error.message, error);
  return ((data ?? []) as unknown as KBArticle[]);
}

/**
 * Fetch a single live article by id, with its author joined, or null when it
 * doesn't exist OR the caller lacks visibility (RLS returns no row). Used by the
 * deep-link (?article=<id>) auto-open path — never throws on not-found.
 */
export async function getArticle(id: string): Promise<KBArticle | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, author:profiles(id,full_name)')
    .eq('id', id)
    .eq('is_deleted', false)
    .maybeSingle();

  if (error) fail(error.message, error);
  return (data as unknown as KBArticle) ?? null;
}
